import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { initializeFixedSocket } from './fixedSocketHelper';
import FixedItinerary from './FixedItinerary';
import FixedWaypointMap from './FixedWaypointMap';
import FixedCurrentWaypoint from './FixedCurrentWaypoint';
import StopActionsModal from './StopActionsModal';

import GeolocationHandler from '../LiveDispatch/GeolocationHandler';
import GpsWarningTag from '../LiveDispatch/GpsWarningTag';
import comms from '../../comms';
import useRouteGroupGPS from '../../../hooks/useRouteGroupGPS';

function FixedShuttlebus({ 
  socket: passedSocket, 
  fleetSettings, 
  userSettings, 
  driverData,
  navigateTo,
  routeDetails,
  selectedVehicle,
  onSaveBreaks,
  onDeleteBreaks,
  onUpdateBreaks,
  onSaveEndPoint,
  onDeleteEndPoint,
  onRouteUpdate,
  isDispatchMode = false 
}) {
  const [jobDetails, setJobDetails] = useState([]);
  const [googleApiKey, setGoogleApiKey] = useState(null);
  const [map, setMap] = useState(null);
  const mapContainerRef = useRef(null);
  const [isAccurate, setIsAccurate] = useState(false);
  const [center, setCenter] = useState(null);
  const [currentEta, setCurrentEta] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isPhone, setIsPhone] = useState(false);
  const [vehiclePosition, setVehiclePosition] = useState(null);

  // Fixed route states (simplified - routeDetails is primary source)
  const [currentStops, setCurrentStops] = useState([]);
  const [isFixedRouteEnabled, setIsFixedRouteEnabled] = useState(false);
  const [nearestStopIndex, setNearestStopIndex] = useState(0);
  const [currentWaypoint, setCurrentWaypoint] = useState(null);
  const [currentScheduleStops, setCurrentScheduleStops] = useState([]);
  
  // State for route completion
  const [isRouteComplete, setIsRouteComplete] = useState(false);
  
  // State for tracking socket updates vs prop updates
  const [hasSocketUpdate, setHasSocketUpdate] = useState(false);
  
  // State for tracking beginning of shift
  const [isBeginShift, setIsBeginShift] = useState(false);

  // StopActionsModal states
  const [showStopActionsModal, setShowStopActionsModal] = useState(false);
  const [selectedActionStop, setSelectedActionStop] = useState(null);
  
  // Break and end point management states
  const [breakLogs, setBreakLogs] = useState([]);
  const [endPointLog, setEndPointLog] = useState(null);
  const [canDeleteBreaks] = useState(true); // Fixed routes can delete breaks by default
  const [canDeleteEndPoint] = useState(true); // Fixed routes can delete endpoints by default
  
  // Connection status state for toast display
  const [connectionError, setConnectionError] = useState(null);
  
  // Refs for GPS timing
  const lastLocationRef = useRef(null);
  
  // Waypoint arrival/departure confirmation states
  const [waypointArrived, setWaypointArrived] = useState(false);

  // Route group GPS — join room, send/receive peer positions, stale cleanup
  const { groupVehicles, sendGPS } = useRouteGroupGPS({
    selectedVehicle,
    routeDetails,
    fleetSettings,
    routeType: 'fixedroute',
  });
  
  // Extract schedule info directly from routeDetails
  const scheduleStatus = 'active'; // Since we have routeDetails, we're in active mode
  const scheduleStartTime = routeDetails?.startTime || null;
  const scheduleEndTime = routeDetails?.endTime || null;
  const fixedRouteData = routeDetails; // Use routeDetails directly
  const currentRun = routeDetails?.itinerary?.[0] ? {
    Id: routeDetails.itinerary[0].RunId,
    Name: routeDetails.itinerary[0].RunName
  } : null;
// selectedVehicle now comes from props (like LoopedShuttlebus)

  // Memoized computed values to prevent infinite re-renders
  const memoizedSchedule = useMemo(() => {
    // For new routeDetails format, check if we have itinerary data
    const hasRouteData = routeDetails && routeDetails.itinerary && routeDetails.itinerary.length > 0;
    const hasScheduleStops = currentScheduleStops && currentScheduleStops.length > 0;
    
    return hasRouteData && hasScheduleStops ?
      currentScheduleStops :
      hasRouteData ?
        [] :
        jobDetails.filter((job) => !job.completed);
  }, [routeDetails, currentScheduleStops, jobDetails]);

  const memoizedCurrentStopIndex = useMemo(() => {
    return currentScheduleStops.length > 0 ?
      currentScheduleStops.findIndex(stop => stop.Id === currentWaypoint?.Id) :
      nearestStopIndex;
  }, [currentScheduleStops, currentWaypoint?.Id, nearestStopIndex]);

  const memoizedCompletedStops = useMemo(() => {
    return currentScheduleStops.filter(stop => stop.isCompleted).map(stop => ({
      stopId: stop.Id,
      runId: stop.runId,
      runName: stop.runName
    }));
  }, [currentScheduleStops]);

  // Subscribe to connection status changes for reconnection handling
  useEffect(() => {
    let wasDisconnected = false;
    
    const unsubscribe = comms.onConnectionStatusChange((status, previousStatus) => {
      console.log('FixedShuttlebus connection status:', status, 'previous:', previousStatus);
      
      // Track disconnection
      if (status === 'disconnected' || status === 'error') {
        wasDisconnected = true;
        setConnectionError(null); // Clear any previous error
      }
      
      // Show error toast on failed status
      if (status === 'failed') {
        setConnectionError('Connection failed. Tap the status icon to reconnect.');
      }
      
      // Clear error on successful connection
      if (status === 'connected') {
        setConnectionError(null);
        
        // Reload route data after reconnection
        if (wasDisconnected && routeDetails?.routeId) {
          console.log('Reconnected - reloading fixed route data...');
          comms.getRouteDetails(routeDetails.routeId)
            .then((updatedRouteData) => {
              console.log('Fixed route data reloaded after reconnection:', updatedRouteData);
              if (updatedRouteData && typeof onRouteUpdate === 'function') {
                onRouteUpdate(updatedRouteData);
              }
            })
            .catch((err) => {
              console.error('Failed to reload fixed route data after reconnection:', err);
            });
        }
        wasDisconnected = false;
      }
    });
    
    return unsubscribe;
  }, [routeDetails?.routeId, onRouteUpdate]);

  // Track whether we're on a phone vs tablet/desktop
  // Respect fleet setting 'displayOrientation' to force portrait or landscape layout
  useEffect(() => {
    const handleResize = () => {
      // Check if fleet setting 'displayOrientation' is set
      if (fleetSettings?.displayOrientation === 'portrait') {
        setIsPhone(true); // Force portrait layout
      } else if (fleetSettings?.displayOrientation === 'landscape') {
        setIsPhone(false); // Force landscape layout
      } else {
        // Default responsive behavior based on window width
        setIsPhone(window.innerWidth < 1200);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fleetSettings?.displayOrientation]);

  useEffect(() => {
    if (googleApiKey) {
      setLoading(false);
    }
  }, [googleApiKey]);

  // Recalculate progress when job details change
  useEffect(() => {
    calculateScheduleProgress();
  }, [jobDetails]);

  // Initialize with routeDetails from authorization
  useEffect(() => {
    if (!routeDetails) {
      return;
    }

    // Reset socket update flag only if this is a fresh route (not from socket)
    if (routeDetails.updateSource !== 'socketUpdate' && hasSocketUpdate) {
      console.log('FixedShuttlebus: Resetting socket update flag for fresh route data');
      setHasSocketUpdate(false);
    }

    // If we have active socket updates, don't override with routeDetails
    if (hasSocketUpdate) {
      console.log('FixedShuttlebus: Skipping route details update - socket data is active');
      return;
    }

    console.log('FixedShuttlebus: Processing route details from props', routeDetails);
    
    if (routeDetails.itinerary) {
      
          // Convert the new itinerary format to the format expected by the component
      const processedStops = routeDetails.itinerary.map((stop, index) => ({
        Id: stop.StopId || stop.ItineraryId,
        Name: stop.StopName || stop.LandmarkName,
        Lat: parseFloat(stop.Latitude),
        Lon: parseFloat(stop.Longitude),
        SortOrder: stop.SortOrder || index + 1,
        ETAOffsetMins: stop.ETAOffsetMins || 0,
        scheduleId: stop.ScheduleId || null,
        // New properties for enhanced functionality
        completedTime: stop.completedTime,
        currentWaypoint: stop.currentWaypoint,
        eta: stop.eta,
        breakStep: stop.breakStep,
        breakTime: stop.breakTime,
        beginStep: stop.beginStep,
        endStep: stop.endStep,
        // Legacy compatibility
        completed: !!stop.completedTime,
        isBreak: stop.breakStep,
        // Additional legacy properties that may be expected
        id: stop.StopId || stop.ItineraryId,
        stopId: stop.StopId || stop.ItineraryId,
        itineraryId: stop.ItineraryId || stop.ItineraryId,
        name: stop.StopName || stop.LandmarkName,
        latitude: parseFloat(stop.Latitude),
        longitude: parseFloat(stop.Longitude),
        sortOrder: stop.SortOrder || index + 1,
        etaOffsetMins: stop.ETAOffsetMins || 0,
        runId: stop.RunId,
        runName: stop.RunName,
        // Add run information at stop level for compatibility
        run: {
          Id: stop.RunId,
          Name: stop.RunName
        }
      }));

      setCurrentStops(processedStops);      // Find current waypoint and set up schedule stops
      const currentIndex = processedStops.findIndex(stop => stop.currentWaypoint);
      if (currentIndex !== -1) {
        setCurrentWaypoint(processedStops[currentIndex]);
        setNearestStopIndex(currentIndex);
        // Set remaining stops as the schedule from current waypoint onward
        setCurrentScheduleStops(processedStops.slice(currentIndex));
      } else {
        // If no current waypoint, find the first incomplete stop
        const nextIncompleteIndex = processedStops.findIndex(stop => !stop.completedTime);
        if (nextIncompleteIndex !== -1) {
          setCurrentWaypoint(processedStops[nextIncompleteIndex]);
          setNearestStopIndex(nextIncompleteIndex);
          setCurrentScheduleStops(processedStops.slice(nextIncompleteIndex));
        } else {
          // All stops completed
          setCurrentWaypoint(null);
          setCurrentScheduleStops([]);
        }
      }

      // If center is still null (GPS hasn't fired yet), use first route stop's coordinates as fallback
      if (!center) {
        const firstStopWithCoords = processedStops.find(s => s.Lat && s.Lon);
        if (firstStopWithCoords) {
          console.log('FixedShuttlebus: GPS not available yet, using first route stop as fallback center:', firstStopWithCoords.Name);
          setCenter({ lat: firstStopWithCoords.Lat, lng: firstStopWithCoords.Lon });
        }
      }

      setIsFixedRouteEnabled(true);
      setLoading(false);
    }
  }, [routeDetails, hasSocketUpdate]);

  // Initialize break logs and end point log from routeDetails (using LoopedShuttlebus pattern)
  useEffect(() => {
    if (routeDetails) {
      // Use server-authoritative data directly (same as LoopedShuttlebus)
      setBreakLogs(routeDetails.breakLogs || []);
      setEndPointLog(routeDetails.endPointLog || null);
    }
  }, [routeDetails]);

  // Initialize isBeginShift based on initial route logs (only for fresh route loads, not socket updates)
  useEffect(() => {
    if (routeDetails && routeDetails.logs && !hasSocketUpdate) {
      const logs = routeDetails.logs;
      if (logs.length === 0) {
        console.log('FixedShuttlebus: Initial load - Setting isBeginShift = true (empty logs)');
        setIsBeginShift(true);
      } else {
        console.log('FixedShuttlebus: Initial load - Setting isBeginShift = false (has logs:', logs.length, ')');
        setIsBeginShift(false);
      }
    } else if (!routeDetails && !hasSocketUpdate) {
      console.log('FixedShuttlebus: Initial load - Setting isBeginShift = true (no route details)');
      setIsBeginShift(true); // Default to true if no route details
    }
  }, [routeDetails, hasSocketUpdate]);

  // Handle GPS errors from GeolocationHandler - fall back to first route stop
  const handleLocationError = useCallback((errorMsg) => {
    console.log('FixedShuttlebus: GPS error received:', errorMsg);
    
    // If center is still null, try to fall back to first route stop
    if (!center && routeDetails?.itinerary?.length > 0) {
      const firstStop = routeDetails.itinerary.find(s => s.Latitude && s.Longitude);
      if (firstStop) {
        console.log('FixedShuttlebus: GPS failed, falling back to first route stop:', firstStop.StopName);
        setCenter({ lat: parseFloat(firstStop.Latitude), lng: parseFloat(firstStop.Longitude) });
      }
    }
  }, [center, routeDetails]);

  const handleLocationUpdate = (newLocation) => {
    // Store the latest location
    lastLocationRef.current = newLocation;
    
    // Immediately update components for real-time functionality
    setCenter(newLocation);
    setVehiclePosition(newLocation); // Update vehicle position for map marker
    if (map) {
      map.panTo(newLocation);
    }

    // Update nearest stop for fixed route (immediate for auto arrive/depart)
    if (isFixedRouteEnabled && currentStops.length > 0) {
      updateNearestStop(newLocation);
    }
    
    // Send GPS update to server (throttled via hook)
    sendGPS(newLocation);
  };

  // Function to check if Fixed route is completed
  const checkFixedRouteCompletion = useCallback(() => {
    // For FixedShuttle, route is complete if the last item in the routeObject.itinerary 
    // where endStep==true has a valid "completedTime" property
    
    if (!routeDetails || !routeDetails.itinerary || routeDetails.itinerary.length === 0) {
      return false;
    }

    // Find all stops with endStep=true (there might be multiple end steps in the route)
    const endStops = routeDetails.itinerary.filter(stop => stop.endStep === true);
    
    if (endStops.length === 0) {
      return false;
    }

    // Get the last end step in the route (by sort order)
    const lastEndStop = endStops.reduce((latest, stop) => {
      const stopOrder = stop.SortOrder || stop.sortOrder || 0;
      const latestOrder = latest.SortOrder || latest.sortOrder || 0;
      return stopOrder > latestOrder ? stop : latest;
    });



    // If this is the current waypoint and it's an endStep, consider it complete
    // This handles the case where the driver is at the final stop but hasn't departed yet
    if (lastEndStop.currentWaypoint && lastEndStop.endStep) {
      return true;
    }

    // Check if it has a valid completedTime (normal completion scenario)
    if (lastEndStop.completedTime && lastEndStop.completedTime.trim() !== '') {
      return true;
    }
    return false;
    
  }, [routeDetails]);

  // Function to update the nearest stop based on current location
  const updateNearestStop = (location) => {
    if (!location || currentStops.length === 0) return;

    let minDistance = Infinity;
    let nearestIndex = 0;

    currentStops.forEach((stop, index) => {
      const stopLat = parseFloat(stop.Lat);
      const stopLon = parseFloat(stop.Lon);

      // Simple distance calculation (not haversine, just for comparison)
      const distance = Math.sqrt(
        Math.pow(location.lat - stopLat, 2) +
        Math.pow(location.lng - stopLon, 2)
      );

      if (distance < minDistance) {
        minDistance = distance;
        nearestIndex = index;
      }
    });

    setNearestStopIndex(nearestIndex);

    // Calculate route progress based on schedule completion
    calculateScheduleProgress();
  };

  // Function to calculate schedule progress based on completed stops vs total stops
  const calculateScheduleProgress = () => {
    // Progress calculation now handled by routeDetails data
    // The backend provides completion status directly
  };

  // NOTE: Old determineCurrentWaypoint useEffect removed - now using handleFixedRouteData approach

  // Helper function to build complete day's schedule from fixed route data
  // NOTE: Old determineCurrentWaypoint useEffect removed - now using handleFixedRouteData approach

  // When we have new ETAs from Google Maps API, update our stops
  const updateStopsWithLiveETAs = (etaData) => {
    if (!etaData || etaData.length === 0) return;

    // Update job details with the new ETA information for non-fixed routes
    if (jobDetails.length > 0 && !isFixedRouteEnabled) {
      const updatedJobDetails = jobDetails.map((job, index) => {
        const matchingEta = etaData.find(eta => eta.index === index);
        if (matchingEta) {
          return {
            ...job,
            trafficBasedEta: matchingEta.trafficBasedDuration,
            estimatedArrival: matchingEta.estimatedArrival,
            // Calculate if we're ahead or behind schedule
            estimatedDelay: matchingEta.durationFromStart - job.scheduledETA
          };
        }
        return job;
      });

      setJobDetails(updatedJobDetails);
    }
  };



  // Handle fixed route data (legacy - kept for socket messages, but routeDetails is primary)
  const handleFixedRouteData = useCallback((fixedRoute) => {

    // Since routeDetails is now the primary source, this handler is mainly for 
    // socket-based updates like completion status changes
    if (!fixedRoute) {
      return;
    }

    // We might use this for real-time updates to completion status
    // But the main route data comes from routeDetails prop
  }, []);

  // Handler for socket-based route updates (enhanced to match LoopedShuttlebus pattern)
  const handleSocketRouteUpdate = useCallback((updateData) => {
    console.log('FixedShuttle - Received socket route update:', updateData);
    console.log('FixedShuttle - Update action:', updateData.action);
    
    // Mark that we have socket updates to prevent prop overrides
    setHasSocketUpdate(true);
    
    // Check if server provided complete route data (preferred approach like LoopedShuttlebus)
    if (updateData.completeRouteData && updateData.completeRouteData.itinerary) {
      console.log('FixedShuttle - Processing complete route data from server');
      
      // Convert server itinerary to currentScheduleStops format with enhanced properties
      const processedStops = updateData.completeRouteData.itinerary.map((stop, index) => ({
        Id: stop.StopId || stop.ItineraryId,
        Name: stop.StopName || stop.LandmarkName,
        Lat: parseFloat(stop.Latitude),
        Lon: parseFloat(stop.Longitude),
        SortOrder: stop.SortOrder || index + 1,
        ETAOffsetMins: stop.ETAOffsetMins || 0,
        scheduleId: stop.ScheduleId || null,
        completedTime: stop.completedTime,
        currentWaypoint: stop.currentWaypoint,
        arrivedAt: stop.arrivedAt,
        completed: stop.completed || !!stop.completedTime,
        passengerCount: stop.passengerCount,
        eta: stop.eta || stop.estimatedArrivalTime,
        // Enhanced properties for break/end point management
        breakStep: stop.breakStep,
        breakTime: stop.breakTime,
        beginStep: stop.beginStep,
        endStep: stop.endStep,
        // Legacy compatibility
        isBreak: stop.breakStep,
        isCompleted: !!stop.completedTime,
        id: stop.StopId || stop.ItineraryId,
        stopId: stop.StopId || stop.ItineraryId,
        itineraryId: stop.ItineraryId || stop.ItineraryId,
        name: stop.StopName || stop.LandmarkName,
        latitude: parseFloat(stop.Latitude),
        longitude: parseFloat(stop.Longitude),
        sortOrder: stop.SortOrder || index + 1,
        etaOffsetMins: stop.ETAOffsetMins || 0,
        runId: stop.RunId,
        runName: stop.RunName,
        run: {
          Id: stop.RunId,
          Name: stop.RunName
        }
      }));

      console.log('FixedShuttle - Processed stops:', processedStops.length);
      setCurrentScheduleStops(processedStops);
      setCurrentStops(processedStops); // Also update main currentStops state
      
      // Update current waypoint from server data (enhanced logic)
      if (updateData.completeRouteData.currentWaypoint) {
        console.log('FixedShuttle - Server provided current waypoint:', updateData.completeRouteData.currentWaypoint);
        setCurrentWaypoint(updateData.completeRouteData.currentWaypoint);
      } else {
        // Find current waypoint from processed stops
        const currentWaypointStop = processedStops.find(stop => stop.currentWaypoint === true);
        if (currentWaypointStop) {
          console.log('FixedShuttle - Found current waypoint in stops:', currentWaypointStop.Name);
          setCurrentWaypoint(currentWaypointStop);
        } else {
          // Find next incomplete stop
          const nextIncompleteStop = processedStops.find(stop => !stop.completedTime && !stop.completed);
          if (nextIncompleteStop) {
            console.log('FixedShuttle - Found next incomplete stop:', nextIncompleteStop.Name);
            setCurrentWaypoint(nextIncompleteStop);
          } else {
            console.log('FixedShuttle - No incomplete stops found - route may be complete');
            setCurrentWaypoint(null);
          }
        }
      }

      // Update break and endpoint logs from server (check multiple possible locations)
      console.log('FixedShuttle - Checking for break logs in completeRouteData update:', {
        hasUpdatedBreakLogs: updateData.updatedBreakLogs !== undefined,
        hasCompleteRouteDataBreakLogs: updateData.completeRouteData?.breakLogs !== undefined,
        updatedBreakLogs: updateData.updatedBreakLogs,
        completeRouteDataBreakLogs: updateData.completeRouteData?.breakLogs
      });
      
      // Check for break logs in multiple possible locations
      let newBreakLogs = undefined;
      if (updateData.updatedBreakLogs !== undefined) {
        newBreakLogs = updateData.updatedBreakLogs;
      } else if (updateData.completeRouteData?.breakLogs !== undefined) {
        newBreakLogs = updateData.completeRouteData.breakLogs;
      }
      
      if (newBreakLogs !== undefined) {
        console.log('FixedShuttle - Updating break logs from complete route data:', newBreakLogs);
        setBreakLogs(newBreakLogs);
        if (onSaveBreaks) onSaveBreaks(newBreakLogs);
      }
      
      // Check for endpoint logs in multiple possible locations  
      let newEndPointLog = undefined;
      if (updateData.updatedEndPointLog !== undefined) {
        newEndPointLog = updateData.updatedEndPointLog;
      } else if (updateData.completeRouteData?.endPointLog !== undefined) {
        newEndPointLog = updateData.completeRouteData.endPointLog;
      }
      
      if (newEndPointLog !== undefined) {
        console.log('FixedShuttle - Updating end point log from complete route data:', newEndPointLog);
        setEndPointLog(newEndPointLog);
        if (onSaveEndPoint) onSaveEndPoint(newEndPointLog);
      }
      
      // Notify parent component of complete route data update
      if (onRouteUpdate && typeof onRouteUpdate === 'function') {
        const updatedRouteData = {
          ...updateData.completeRouteData,
          updateSource: 'socketUpdate',
          lastUpdated: new Date().toISOString()
        };
        
        // Check if this update indicates route completion
        const hasEndStepCompleted = processedStops.some(stop => 
          stop.endStep === true && (stop.completedTime || stop.completed)
        );
        
        if (hasEndStepCompleted) {
          updatedRouteData.isComplete = true;
          updatedRouteData.completionDetectedAt = new Date().toISOString();
        }
        
        console.log('FixedShuttle - Notifying parent of route update');
        onRouteUpdate(updatedRouteData);
      }
      
      // Update isBeginShift based on completeRouteData.logs
      if (updateData.completeRouteData.logs !== undefined) {
        const logs = updateData.completeRouteData.logs;
        if (logs.length === 0) {
          console.log('FixedShuttle: Setting isBeginShift = true (empty logs)');
          setIsBeginShift(true);
        } else {
          console.log('FixedShuttle: Setting isBeginShift = false (has logs:', logs.length, ')');
          setIsBeginShift(false);
        }
      }
      
      // Handle arrival/departure actions even when we have complete route data
      // The server sends both completeRouteData AND the action in the same update
      if (updateData.action === "ARRIVE"){
         console.log('FixedShuttle - Setting waypoint arrived = true (from completeRouteData)');
         setWaypointArrived(true);
      } else if (updateData.action === "DEPART") {
          console.log('FixedShuttle - Setting waypoint arrived = false (from completeRouteData)');
          setWaypointArrived(false);
      }
      
     return; // Exit early when we have complete route data (actions handled above)
    }
    
    // Handle individual break/endpoint log updates (fallback for partial updates)
    if (updateData.updatedBreakLogs !== undefined) {
      console.log('FixedShuttle - Updating break logs (partial update):', updateData.updatedBreakLogs);
      setBreakLogs(updateData.updatedBreakLogs);
      if (onSaveBreaks) {
        onSaveBreaks(updateData.updatedBreakLogs);
      }
    }
    
    if (updateData.updatedEndPointLog !== undefined) {
      console.log('FixedShuttle - Updating end point log (partial update):', updateData.updatedEndPointLog);
      setEndPointLog(updateData.updatedEndPointLog);
      if (onSaveEndPoint) {
        onSaveEndPoint(updateData.updatedEndPointLog);
      }
    }
    
    // Handle arrival/departure status and waypoint advancement
    if (updateData.action === "ARRIVE"){
       console.log('FixedShuttle - Setting waypoint arrived = true');
       setWaypointArrived(true);
    } else if (updateData.action === "DEPART") {
        console.log('FixedShuttle - Setting waypoint arrived = false');
        setWaypointArrived(false);
        
        // Handle waypoint advancement for DEPART action
        if (updateData.stopId && currentScheduleStops.length > 0) {
          console.log('FixedShuttle - Processing waypoint advancement for DEPART from stop:', updateData.stopId);
          
          // Mark the current stop as completed and advance to next stop
          const updatedStops = currentScheduleStops.map(stop => {
            if (stop.Id === updateData.stopId || stop.stopId === updateData.stopId || stop.itineraryId === updateData.itineraryId) {
              return {
                ...stop,
                completed: true,
                isCompleted: true,
                completedTime: updateData.timestamp || new Date().toISOString(),
                passengerCount: updateData.passengerCount || 0
              };
            }
            return stop;
          });
          
          // Find next incomplete stop
          const nextIncompleteStop = updatedStops.find(stop => !stop.completed && !stop.completedTime);
          
          console.log('FixedShuttle - Next incomplete stop:', nextIncompleteStop?.Name || 'None found');
          
          // Update states
          setCurrentScheduleStops(updatedStops);
          setCurrentStops(updatedStops);
          
          if (nextIncompleteStop) {
            setCurrentWaypoint(nextIncompleteStop);
          } else {
            console.log('FixedShuttle - All stops completed');
            setCurrentWaypoint(null);
          }
        }
    }
    
    // Handle isBeginShift for ARRIVE actions and partial updates with logs data
    if (updateData.action === "ARRIVE") {
      console.log('FixedShuttle: Arrive action detected, setting isBeginShift to false');
      setIsBeginShift(false);
    }
    
    // Also check if we have logs data in the partial update to determine isBeginShift state
    if (updateData.logs !== undefined) {
      const logs = updateData.logs;
      if (logs.length === 0) {
        console.log('FixedShuttle: Setting isBeginShift = true (empty logs from partial update)');
        setIsBeginShift(true);
      } else {
        console.log('FixedShuttle: Setting isBeginShift = false (has logs from partial update:', logs.length, ')');
        setIsBeginShift(false);
      }
    }
     
  }, [onRouteUpdate, onSaveBreaks, onSaveEndPoint, currentScheduleStops]);

  useEffect(() => {
    const initialize = async () => {
      if (!fleetSettings) return;

      const driverAppConfig = fleetSettings;
      setGoogleApiKey(driverAppConfig.googleApiKey);

      // Always initialize socket for live updates, but skip initial data loading if we have route data
      const hasRouteSelection = fleetSettings.routeSelection === true;
      const hasRouteDetailsData = routeDetails && routeDetails.itinerary;
      const skipInitialDataLoad = hasRouteSelection && hasRouteDetailsData;
      
      let localSocket = passedSocket || null;
      const cleanup = await initializeFixedSocket(
        localSocket,
        setJobDetails,
        userSettings,
        fleetSettings,
        handleFixedRouteData,
        isDispatchMode,
        handleSocketRouteUpdate, // Add socket update callback
        skipInitialDataLoad // Pass flag to skip initial data loading
      );
      return cleanup;
    };

    initialize();
  }, [passedSocket, fleetSettings, userSettings, handleFixedRouteData, isDispatchMode, routeDetails, handleSocketRouteUpdate]);



  // Socket event listener setup is now handled by fixedSocketHelper
  // No need for duplicate listener here since we pass handleSocketRouteUpdate callback to initializeFixedSocket



  // Monitor for route completion - enhanced monitoring with multiple triggers
  useEffect(() => {
    const isComplete = checkFixedRouteCompletion();
    
    if (isComplete !== isRouteComplete) {
      setIsRouteComplete(isComplete);
      
      // Notify parent component of route completion status change
      if (onRouteUpdate && typeof onRouteUpdate === 'function' && routeDetails) {
        const completionUpdate = {
          ...routeDetails,
          isComplete: isComplete,
          completionStatusUpdated: new Date().toISOString(),
          updateSource: 'routeCompletionMonitor',
          lastUpdated: new Date().toISOString()
        };
        
        onRouteUpdate(completionUpdate);
      }
    }
  }, [
    checkFixedRouteCompletion, 
    isRouteComplete, 
    routeDetails, 
    onRouteUpdate,
    // Add additional dependencies that might affect completion
    currentStops,
    currentScheduleStops,
    endPointLog
  ]);

  // Route completion is now handled by parent Dispatch component via onRouteUpdate
  // No direct navigation needed - parent will detect completion and route to CompleteShift

  const onLoad = useCallback((loadedMap) => {
    setMap(loadedMap);
  }, []);

  const displayTripInfo = (jobNumber) => {
  };

  const geolocationSuccess = (position) => {
    const { latitude, longitude } = position.coords;
    const newLocation = { lat: latitude, lng: longitude };
    setCenter(newLocation);
    setVehiclePosition(newLocation); // Update vehicle position for map marker
  };

  const handleDirectionsCalculated = (duration) => {
    if (jobDetails[0]) {
      const now = new Date();
      const eta = new Date(now.getTime() + duration * 1000);
      setCurrentEta(eta.toLocaleTimeString());
    }
  };

  // StopActionsModal handlers
  const handleOpenStopActions = (stop) => {
    setSelectedActionStop(stop);
    setShowStopActionsModal(true);
  };

  const handleCloseStopActionsModal = () => {
    setShowStopActionsModal(false);
    setSelectedActionStop(null);
  };

  // Helper function to convert time string to ISO string
  const convertToISOString = (timeString) => {
    if (!timeString) return new Date().toISOString();
    
    if (timeString.includes('T')) {
      // Already an ISO string
      return timeString;
    }
    
    // Assume it's a time string like "14:30"
    const today = new Date();
    const [hours, minutes] = timeString.split(':').map(num => parseInt(num, 10));
    today.setHours(hours, minutes, 0, 0);
    return today.toISOString();
  };

  const handleScheduleBreak = async (breakData) => {
    try {
      // Get route information from routeDetails
      const driverId = breakData.logData?.driverId;
      const stopId = breakData.logData?.stopId;
      const stopName = breakData.logData?.stopName;
      const itineraryId = breakData.logData?.itineraryId;
      const routeId = routeDetails?.routeId;
      const runId = routeDetails?.runId;
      const vehicleId = selectedVehicle?.id;
      const scheduleId = breakData.logData?.scheduleId || routeDetails?.scheduleId;

      if (!vehicleId || !routeId || !runId || !driverId || !scheduleId) {
        console.error("Missing required route information:", {
          vehicleId,
          routeId,
          runId,
          driverId,
          scheduleId,
        });
        throw new Error(
          "Missing required route information. Please try again after the route data loads."
        );
      }

      // Send break scheduling message via socket using comms module
      const breakMessage = {
        type: "FIXED_ROUTE_BREAK",
        vehicleId: vehicleId,
        routeId: routeId,
        runId: runId,
        scheduleId: scheduleId,
        itineraryId: itineraryId,
        driverId: driverId,
        stopId: stopId,
        stopName: stopName,
        breakDuration: breakData.logData?.breakDuration || breakData.breakDuration,
        departureTime: breakData.logData?.departureTime || convertToISOString(breakData.departureTime),
        scheduledAt: new Date().toISOString(),
        location: breakData.currentLocation,
      };

      // Use comms module to send the message
      const messageString = `FIXED_ROUTE_BREAK|${JSON.stringify(breakMessage)}`;
      comms.sendSocket(messageString);

      // Immediately add the break to local state for instant UI feedback
      const newBreakLog = {
        Id: `temp_${Date.now()}`, // Temporary ID until server confirms
        StopId: stopId,
        StopName: stopName,
        ItineraryId: itineraryId,
        BreakDuration: breakData.logData?.breakDuration || breakData.breakDuration,
        DepartureTime: breakData.logData?.departureTime || convertToISOString(breakData.departureTime),
        CreatedOn: new Date().toISOString(),
        UpdatedOn: breakData.logData?.departureTime || convertToISOString(breakData.departureTime),
        PAXBoarded: -2, // Break indicator
        VehicleId: vehicleId,
        DriverId: driverId,
      };

      setBreakLogs([...breakLogs, newBreakLog]);
    } catch (error) {
      throw error; // Re-throw to let the modal handle the error display
    }
  };



  const handleDeleteBreak = async (breakData) => {
          const routeId = routeDetails?.routeId;
      const runId = routeDetails?.runId;
      const vehicleId = selectedVehicle?.id;
        const scheduleId = breakData.logData?.scheduleId || routeDetails?.scheduleId;
           const driverId = driverData.DriverId;
    try {
      // Send delete break message via socket
      const deleteMessage = {
        type: "DELETE_FIXED_ROUTE_BREAK",
        logId: breakData.breakLogId || breakData.Id, // Use logId to match looped route handler expectation
        stopId: breakData.stopId,
        itineraryId: breakData.itineraryId,
        runId: runId,
        scheduleId: scheduleId,
        driverId: driverId,
        vehicleId: vehicleId,
        routeId: routeId,
      };

      const messageString = `DELETE_FIXED_ROUTE_BREAK|${JSON.stringify(deleteMessage)}`;
      comms.sendSocket(messageString);

      // Remove from local state immediately
      setBreakLogs(breakLogs.filter(log => log.Id !== (breakData.breakLogId || breakData.Id)));
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteEndPoint = async (endLog) => {
      const routeId = routeDetails?.routeId;
      const vehicleId = selectedVehicle?.id;
      
      try {
      // Send delete end point message via socket
      const deleteMessage = {
        type: "DELETE_FIXED_ROUTE_END",
        logId: endLog.Id, // Use logId to match looped route handler expectation
        vehicleId:  vehicleId,
        routeId: routeId,
      };

      const messageString = `DELETE_FIXED_ROUTE_END|${JSON.stringify(deleteMessage)}`;
      comms.sendSocket(messageString);

      // Remove from local state immediately
      setEndPointLog(null);
    } catch (error) {
      throw error;
    }
  };

  const handleRequestDirections = (requestCallback, showDirectionsCallback) => {
    if (isFixedRouteEnabled && currentStops.length > 0) {
      // For fixed routes, we handle directions in WaypointMap component
      showDirectionsCallback(true);
    }
    else if (jobDetails[0] && center && isAccurate) {
      // For regular jobs/dispatch mode
      const waypoint = jobDetails[0];
      const destination = {
        lat: parseFloat(waypoint.location[0]),
        lng: parseFloat(waypoint.location[1]),
      };

      const newRequest = {
        origin: center,
        destination: destination,
        travelMode: 'DRIVING',
      };

      requestCallback(newRequest);
      showDirectionsCallback(true);
    } else {
      console.error('Cannot request directions: Missing waypoint, center, or accuracy.');
    }
  };

  const handleLiveETAsUpdate = (etaData) => {
    updateStopsWithLiveETAs(etaData);
  };

  const handleMapMoved = (/* newCenter */) => {
    // Optionally handle map movement here
  };


  const handleStopCompleted = useCallback((completedStop, passengerCount) => {
    // The actual state updates will be handled by the socket listener 
    // when the backend sends the fixedRouteStopUpdate message
    
    // Note: End step completion is now handled by handleEndpointCompleted
    console.log('Fixed route stop completed:', completedStop);
  }, []);

  // Handle when endpoint is completed and navigate to CompleteShift (same pattern as LoopedShuttle)
  const handleEndpointCompleted = useCallback(() => {
    console.log('FixedShuttlebus: Endpoint completed - navigating to CompleteShift');
    
    // Force a route completion check after a short delay to allow state updates
    setTimeout(() => {
      const isComplete = checkFixedRouteCompletion();
      
      if (isComplete && !isRouteComplete) {
        setIsRouteComplete(true);
        
        // Immediately notify parent component of completion
        if (onRouteUpdate && typeof onRouteUpdate === 'function' && routeDetails) {
          onRouteUpdate({
            ...routeDetails,
            isComplete: true,
            completionStatusUpdated: new Date().toISOString(),
            updateSource: 'endStepCompletion',
            lastUpdated: new Date().toISOString()
          });
        }
      }
      
      // Navigate to CompleteShift component when endpoint is reached
      console.log('Fixed route endpoint completed - navigating to CompleteShift');
      if (navigateTo && typeof navigateTo === 'function') {
        navigateTo('CompleteShift');
      }
    }, 500); // 500ms delay to allow server updates
  }, [checkFixedRouteCompletion, isRouteComplete, onRouteUpdate, routeDetails, navigateTo]);

  if (loading) {
    return (
      <div className="bg-slate-100 flex items-center justify-center" style={{ height: `var(--app-content-height)` }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-lg font-medium text-gray-700">
            Loading {isDispatchMode ? 'dispatch' : 'fixed route'} mode...
          </div>
        </div>
      </div>
    );
  }

  const containerClass = isPhone
    ? 'flex flex-col'
    : 'flex flex-row h-full';

  const mapSectionStyle = isPhone
    ? {
      height: '500px', // Fixed pixel height for Google Maps on mobile
      padding: '5px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }
    : {
      padding: '5px',
      flex: '1',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    };

  const currentStopSectionStyle = isPhone
    ? {
      height: '250px', // Fixed pixel height for mobile
      paddingTop: '5px',
      paddingLeft: '5px',
      paddingRight: '5px'
    }
    : {
      height: '50%', // 50% of left side in landscape
      padding: '5px'
    };

  const itinerarySectionStyle = isPhone
    // 620px was (65px nav + 555px of chrome above this section). The nav portion now
    // comes from --app-content-height, which also accounts for the iOS safe-area inset
    // the old literal ignored; only the in-screen 555px remains.
    ? { height: 'calc(var(--app-content-height) - 555px)', width: '100%', minHeight: '200px' }
    : { flex: '1', height: '50%', overflowY: 'auto' };

  return (
      <div className="bg-slate-100" style={{ height: `var(--app-content-height)` }}>
        {/* Connection error toast */}
        {connectionError && (
          <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-50 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm">
            {connectionError}
          </div>
        )}
        
        <GeolocationHandler
          onLocationUpdate={handleLocationUpdate}
          onLocationError={handleLocationError}
          setIsAccurate={setIsAccurate}
        />

        {googleApiKey && center && (
          <div className={containerClass}>
            {/* Map section */}
            <div ref={mapContainerRef} style={mapSectionStyle}>
              <FixedWaypointMap
                googleApiKey={googleApiKey}
                isPhone={isPhone}
                center={center}
                jobs={isFixedRouteEnabled && currentScheduleStops.length > 0 ? currentScheduleStops : jobDetails}
                vehiclePosition={vehiclePosition}
                currentWaypoint={currentWaypoint || jobDetails[0]}
                expectedTime={currentEta}
                onDirectionsCalculated={handleDirectionsCalculated}
                isAccurate={isAccurate}
                handleRequestDirections={handleRequestDirections}
                allStops={isFixedRouteEnabled ? (currentScheduleStops.length > 0 ? currentScheduleStops : currentStops) : []}
                isFixedRoute={isFixedRouteEnabled}
                onLiveETAsUpdate={handleLiveETAsUpdate}
                onMapMoved={handleMapMoved}
                currentScheduleStops={currentScheduleStops}
                scheduleStatus={scheduleStatus}
                onMapLoad={onLoad}
                fixedRouteData={fixedRouteData}
                groupVehicles={groupVehicles}
                isBeginShift={isBeginShift}
                appLocation={vehiclePosition}
                routeDetails={routeDetails}
                fleetSettings={fleetSettings}
              />
            </div>

            {/* Right side - Current Stop and Itinerary (desktop) or stacked sections (mobile) */}
            {!isPhone && (
              <div className="flex flex-col" style={{ flex: '1' }}>
                {/* Current Stop Section - 1/2 of right side */}
                {(currentWaypoint || jobDetails[0]) && (
                  <div style={currentStopSectionStyle}>
                    <FixedCurrentWaypoint
                      waypoint={currentWaypoint || jobDetails[0]}
                      expectedTime={currentEta}
                      isAccurate={isAccurate}
                      isPhone={isPhone}
                      vehiclePosition={vehiclePosition}
                      currentLocation={center} // Use center as current location
                      currentRun={currentRun}
                      fixedRouteData={fixedRouteData}
                      userSettings={userSettings}
                      selectedVehicle={selectedVehicle || userSettings?.dispatchData?.vehicleObject}
                      routeDetails={routeDetails}
                      breakLogs={breakLogs} // Server-authoritative break logs
                      endPointLog={endPointLog} // Server-authoritative endpoint log
                      waypointArrived={waypointArrived} // Server confirmation of arrival
                      onStopCompleted={handleStopCompleted}
                      onEndpointCompleted={handleEndpointCompleted} // Add endpoint completion handler
                    />
                  </div>
                )}

                {/* Itinerary section - remaining 1/2 of right side */}
                <div id="itinerary" className="flex flex-col" style={itinerarySectionStyle}>

                  <FixedItinerary
                    isPhone={isPhone}
                    schedule={memoizedSchedule}
                    onStopSelect={(index, stop) => displayTripInfo(index)}
                    onStopAction={handleOpenStopActions}
                    onStopActionsSelect={handleOpenStopActions} // Add row-click activation
                    currentStopIndex={memoizedCurrentStopIndex}
                    completedStops={memoizedCompletedStops}
                    scheduleStatus={scheduleStatus}
                    scheduleStartTime={scheduleStartTime}
                    scheduleEndTime={scheduleEndTime}
                    fixedRouteData={fixedRouteData}
                    currentRunSchedule={null}
                    breakLogs={breakLogs} // Add server-authoritative break logs
                    endPointLog={endPointLog} // Add server-authoritative endpoint log
                  />
                </div>
              </div>
            )}

            {/* Mobile layout - stacked sections */}
            {isPhone && (
              <>
                {/* Current Stop Section */}
                {(currentWaypoint || jobDetails[0]) && (
                  <div style={currentStopSectionStyle}>
                    <FixedCurrentWaypoint
                      waypoint={currentWaypoint || jobDetails[0]}
                      expectedTime={currentEta}
                      isAccurate={isAccurate}
                      isPhone={isPhone}
                      vehiclePosition={vehiclePosition}
                      currentLocation={center} // Use center as current location
                      currentRun={currentRun}
                      fixedRouteData={fixedRouteData}
                      userSettings={userSettings}
                      selectedVehicle={selectedVehicle || userSettings?.dispatchData?.vehicleObject}
                      routeDetails={routeDetails}
                      breakLogs={breakLogs} // Server-authoritative break logs
                      endPointLog={endPointLog} // Server-authoritative endpoint log
                      waypointArrived={waypointArrived} // Server confirmation of arrival
                      onStopCompleted={handleStopCompleted}
                      onEndpointCompleted={handleEndpointCompleted} // Add endpoint completion handler
                    />
                  </div>
                )}

                {/* Itinerary section */}
                <div id="itinerary" className="flex flex-col" style={itinerarySectionStyle}>

                  <FixedItinerary
                    isPhone={isPhone}
                    schedule={memoizedSchedule}
                    onStopSelect={(index, stop) => displayTripInfo(index)}
                    onStopAction={handleOpenStopActions}
                    onStopActionsSelect={handleOpenStopActions} // Add row-click activation
                    currentStopIndex={memoizedCurrentStopIndex}
                    completedStops={memoizedCompletedStops}
                    scheduleStatus={scheduleStatus}
                    scheduleStartTime={scheduleStartTime}
                    scheduleEndTime={scheduleEndTime}
                    fixedRouteData={fixedRouteData}
                    currentRunSchedule={null}
                    breakLogs={breakLogs} // Add server-authoritative break logs
                    endPointLog={endPointLog} // Add server-authoritative endpoint log
                  />
                </div>
              </>
            )}
          </div>
        )}

        {!isAccurate && (
          <GpsWarningTag onLocationUpdate={handleLocationUpdate} setIsAccurate={setIsAccurate} />
        )}

        {/* StopActionsModal */}
        <StopActionsModal
          isOpen={showStopActionsModal}
          onClose={handleCloseStopActionsModal}
          selectedStop={selectedActionStop}
          currentLocation={center}
          currentETA={currentEta}
          onScheduleBreak={handleScheduleBreak}
          scheduledCompletionInfo={endPointLog ? {
            stopId: endPointLog.StopId,
            completionTime: endPointLog.UpdatedOn
          } : null}
          breakLogs={breakLogs}
          onSetBreakLogs={setBreakLogs}
          endPointLog={endPointLog}
          onSetEndPointLog={setEndPointLog}
          routeDetails={routeDetails}
          canDeleteBreaks={canDeleteBreaks}
          canDeleteEndPoint={canDeleteEndPoint}
          isOnBreak={breakLogs.length > 0}
          onDeleteBreak={handleDeleteBreak}
          onDeleteEndPoint={handleDeleteEndPoint}
          driverData={driverData}
          userSettings={userSettings}
          selectedVehicle={userSettings?.dispatchData?.vehicleObject}
          routeData={currentStops}
        />
      </div>
  );
}

export default FixedShuttlebus;
